<template>
  <div
    v-if="isVisible"
    class="highlight-modal-backdrop"
    @click="handleBackdropClick"
  >
    <div class="highlight-modal-content" @click.stop>
      <button
        type="button"
        class="modal-close-btn"
        @click="close"
        aria-label="Close"
      >
        <span class="material-icons">close</span>
      </button>

      <div class="modal-body-content">
        <div class="text-center">
          <!-- Icon -->
          <div class="highlight-icon-wrapper mb-3">
            <img :src="iconUrl" :alt="highlight?.type" class="highlight-icon" />
          </div>

          <!-- Title -->
          <h4 class="mb-1">{{ highlight?.title || 'Custom POI' }}</h4>

          <!-- Type and Day -->
          <p class="text-muted mb-3">
            <small>{{ prettyTypeName }}</small>
            <span v-if="highlight?.dayNumber" class="ms-2">• Day {{ highlight.dayNumber }}</span>
          </p>

          <!-- Description -->
          <p v-if="highlight?.description" class="mb-3 description-text">
            {{ highlight.description }}
          </p>

          <!-- Image Gallery -->
          <div v-if="hasImages" class="mb-4">
            <div v-if="images.length === 1" class="single-image-container">
              <img
                :src="images[0].file?.lgUrl || images[0].url"
                :alt="highlight?.title"
                class="img-fluid rounded highlight-image"
                @click="openLightbox(0)"
                style="cursor: pointer; max-height: 400px; width: auto; margin: 0 auto;"
              />
            </div>
            <div v-else class="image-gallery">
              <div class="row g-2">
                <div
                  v-for="(image, index) in images.slice(0, 4)"
                  :key="index"
                  :class="images.length === 2 ? 'col-6' : images.length === 3 && index === 0 ? 'col-12' : 'col-6'"
                >
                  <div class="position-relative">
                    <img
                      :src="image.file?.mdUrl || image.file?.lgUrl || image.url"
                      :alt="`${highlight?.title} - Image ${index + 1}`"
                      class="img-fluid rounded highlight-image"
                      @click="openLightbox(index)"
                      style="cursor: pointer; width: 100%; height: 200px; object-fit: cover;"
                    />
                    <div
                      v-if="images.length > 4 && index === 3"
                      class="overlay-more"
                      @click="openLightbox(3)"
                    >
                      <span class="text-white fs-4">+{{ images.length - 4 }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, nextTick } from 'vue';
import { POIUtility } from '../../../utils/models/POI';

export default defineComponent({
  name: 'EmbedHighlightDetailModal',
  setup() {
    const isVisible = ref(false);
    const highlight = ref<any>(null);

    const prettyTypeName = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.prettyName();
      } catch (e) {
        return highlight.value.type;
      }
    });

    const iconUrl = computed(() => {
      if (!highlight.value?.type) return '';
      try {
        const poiUtil = POIUtility.fromString(highlight.value.type);
        return poiUtil.getPublicImageIcon();
      } catch (e) {
        // Return a default icon
        return POIUtility.fromString('other').getPublicImageIcon();
      }
    });

    const images = computed(() => {
      if (!highlight.value?.images) return [];
      return highlight.value.images.filter((image: any) => !image._destroy);
    });

    const hasImages = computed(() => images.value.length > 0);

    const openLightbox = async (startIndex: number) => {
      await nextTick();

      const lightboxContainer = document.createElement('div');
      lightboxContainer.className = 'highlight-lightbox';
      lightboxContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.95);
        z-index: 99999;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
      `;

      let currentIndex = startIndex;

      const updateImage = () => {
        const img = images.value[currentIndex];
        const imgUrl = img.file?.lgUrl || img.url;
        lightboxContainer.innerHTML = `
          <div style="position: relative; max-width: 90%; max-height: 90%;">
            <img src="${imgUrl}" style="max-width: 100%; max-height: 90vh; object-fit: contain;" />
            ${images.value.length > 1 ? `
              <button class="lightbox-btn lightbox-prev" style="position: absolute; top: 50%; left: 10px; transform: translateY(-50%); background: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex; align-items: center; justify-content: center;">
                <span class="material-icons" style="font-size: 24px; color: #000;">chevron_left</span>
              </button>
              <button class="lightbox-btn lightbox-next" style="position: absolute; top: 50%; right: 10px; transform: translateY(-50%); background: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex; align-items: center; justify-content: center;">
                <span class="material-icons" style="font-size: 24px; color: #000;">chevron_right</span>
              </button>
              <div style="position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); background: rgba(0, 0, 0, 0.7); color: white; padding: 8px 16px; border-radius: 20px; font-size: 14px;">
                ${currentIndex + 1} / ${images.value.length}
              </div>
            ` : ''}
            <button class="lightbox-close" style="position: absolute; top: 10px; right: 10px; background: rgba(255, 255, 255, 0.9); border: none; border-radius: 50%; width: 40px; height: 40px; cursor: pointer; display: flex; align-items: center; justify-content: center;">
              <span class="material-icons" style="font-size: 24px; color: #000;">close</span>
            </button>
          </div>
        `;

        const closeBtn = lightboxContainer.querySelector('.lightbox-close');
        const prevBtn = lightboxContainer.querySelector('.lightbox-prev');
        const nextBtn = lightboxContainer.querySelector('.lightbox-next');

        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            document.body.removeChild(lightboxContainer);
          });
        }

        if (prevBtn && images.value.length > 1) {
          prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
            updateImage();
          });
        }

        if (nextBtn && images.value.length > 1) {
          nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            currentIndex = (currentIndex + 1) % images.value.length;
            updateImage();
          });
        }
      };

      updateImage();

      lightboxContainer.addEventListener('click', (e) => {
        if (e.target === lightboxContainer) {
          document.body.removeChild(lightboxContainer);
        }
      });

      const handleKeydown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          document.body.removeChild(lightboxContainer);
          document.removeEventListener('keydown', handleKeydown);
        } else if (e.key === 'ArrowLeft' && images.value.length > 1) {
          currentIndex = (currentIndex - 1 + images.value.length) % images.value.length;
          updateImage();
        } else if (e.key === 'ArrowRight' && images.value.length > 1) {
          currentIndex = (currentIndex + 1) % images.value.length;
          updateImage();
        }
      };

      document.addEventListener('keydown', handleKeydown);
      document.body.appendChild(lightboxContainer);
    };

    const showHighlight = (highlightData: any) => {
      highlight.value = highlightData;
      isVisible.value = true;
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      isVisible.value = false;
      document.body.style.overflow = '';
    };

    const handleBackdropClick = () => {
      close();
    };

    return {
      isVisible,
      highlight,
      prettyTypeName,
      iconUrl,
      images,
      hasImages,
      openLightbox,
      showHighlight,
      close,
      handleBackdropClick
    };
  }
});
</script>

<style scoped>
.highlight-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.highlight-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-close-btn {
  position: absolute;
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: background 0.2s ease;
}

.modal-close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.modal-close-btn .material-icons {
  font-size: 20px;
  color: #333;
}

.modal-body-content {
  padding: 48px 32px 32px;
}

.highlight-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 50%;
}

.highlight-icon {
  width: 48px;
  height: 48px;
  object-fit: contain;
}

.description-text {
  color: #333;
  line-height: 1.6;
}

.text-muted {
  color: #6c757d !important;
}

.highlight-image {
  transition: transform 0.2s;
}

.highlight-image:hover {
  transform: scale(1.02);
}

.overlay-more {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 0.25rem;
}

.image-gallery {
  max-width: 500px;
  margin: 0 auto;
}

.single-image-container {
  text-align: center;
}

.row {
  display: flex;
  flex-wrap: wrap;
  margin-right: -0.5rem;
  margin-left: -0.5rem;
}

.g-2 > * {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.col-6 {
  flex: 0 0 auto;
  width: 50%;
}

.col-12 {
  flex: 0 0 auto;
  width: 100%;
}

.position-relative {
  position: relative;
}

.rounded {
  border-radius: 0.25rem !important;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.ms-2 {
  margin-left: 0.5rem;
}

.text-center {
  text-align: center;
}

.img-fluid {
  max-width: 100%;
  height: auto;
}

h4 {
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
}

small {
  font-size: 0.875em;
}
</style>
